import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import OnePageSpread from 'assets/icons/OnePageSpread';
import TwoPageSpread from 'assets/icons/TwoPageSpread';
import PageToggleButton from 'components/@common/Buttons/PageToggleButton';
import ZoomButton from 'components/@common/Buttons/ZoomButton/ZoomButton';
import useIdleTimeout from 'state/hooks/use-idle-timer';
import { useFullScreen } from 'state/providers/FullScreenProvider';
import { useZoom, MAX_ZOOM_SCALE, MIN_ZOOM_SCALE, ZoomTypes } from 'state/providers/ZoomProvider';
import { Workbook, PageDetail, AppStoreState } from 'types';
import PageNavButton from './PageNavButton';
import PageSelector from './PageSelector';
import TrialBadge from '../Badges/TrialBadge';
import BasicButton from '../@common/Buttons/BasicButton';
import maybe from '../@common/enhancers/maybe';
import { remCalc } from '../../util/style-util';
import { getRoutingForPage, getRoutingForNewBookPage } from '../../util/workbook-util';
import { useTheme } from '@mui/material';

type Props = {
  totalPages: number;
  workbook: Workbook;
  pageOffset: number;
  isZoom: boolean;
  toggleMenu?: () => void;
  children?: any;
  isTrialLicense: boolean;
  trialDaysLeft: number;
  singlePage: boolean;
  pageDetails: Array<PageDetail>;
  navBarDisabled: boolean;
  queueTour?: (name: string) => void;
  showTrialModal?: () => void;
  cancelDownloadRequest: () => void;
} & any;

const MaybeTrialBadge: any = maybe(TrialBadge);

export const PageNavBar = ({
  totalPages,
  workbook,
  pageOffset,
  isZoom,
  toggleMenu,
  children: Menu,
  isTrialLicense,
  trialDaysLeft,
  singlePage,
  showTrialModal,
  cancelDownloadRequest,
  navBarDisabled = false
}: Props | any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { pageNumber, bookCode, edition } = params;
  const loading = useSelector(({ entity }: AppStoreState) => entity.loading, shallowEqual);

  const { isIdle } = useIdleTimeout({});
  const [{ isFullScreen, screen1Handle }, { setFullScreen }] = useFullScreen();
  const [{ zoomScale }, { increaseZoom, decreaseZoom, resetZoom }] = useZoom();

  const shouldShowSwitcher = showSwitcher(workbook, isZoom);
  const theme = useTheme();

  const {
    fullScreenNavBar,
    navBar,
    reminder,
    verticalLine,
    pageToggleContainer,
    onePageToggle,
    twoPageToggle,
    stopButton,
    switcheroo,
    pageZoom,
    zoomLevelText,
    mainNavBarContent
  } = useStyles(theme)({ shouldShowSwitcher, isIdle, workbook, isZoom });
  const currentPage = +pageNumber!;

  const turnPage = (moveForward: boolean) => () => {
    if (!loading) {
      const changePageBy = isZoom || singlePage ? 1 : 2;
      const newPage = currentPage + (moveForward ? changePageBy : -changePageBy);
      const { path, replace } = getRoutingForPage(
        newPage,
        { params, url: location.pathname },
        isZoom,
        true
      );
      //cancel current download requests so that we don't pull workbook pages we don't need anymore
      if (window.stop !== undefined) {
        window.stop();
        cancelDownloadRequest();
      } else if (document.execCommand !== undefined) {
        document.execCommand('Stop', false);
        cancelDownloadRequest();
      }

      return navigate(path, { replace });
    }
  };

  const switchPageView = (isOnePage: boolean) => {
    if (isNaN(currentPage)) return;

    let currPage = currentPage;

    if (!isOnePage) currPage = currentPage % 2 !== 0 ? currentPage - 1 : currentPage;

    const url = `/books/${String(bookCode)}/${String(edition)}/${currPage}${
      isOnePage ? '/zoom' : ''
    }`;

    navigate(url);
  };

  const switchBooks = () => {
    const { pageDetails } = workbook;

    if (pageDetails && pageDetails.length) {
      let path;
      if (pageDetails[0]?.workbookPage) {
        path = getRoutingForNewBookPage(
          pageDetails[0]?.workbookPage.pageNumber,
          params,
          workbook?.workbooks[0]?.bookCode
        );
      } else {
        path = getRoutingForNewBookPage(
          pageDetails[0]?.teachersGuidePage?.pageNumber,
          params,
          workbook?.teachersGuides[0]?.bookCode
        );
      }
      resetZoom();
      navigate(path.path, { replace: true });
    }
  };

  const nextPageDisabled = (): boolean => {
    const remainingPages = isZoom || singlePage ? 1 : 2;

    return (
      currentPage >= totalPages ||
      (totalPages % 2 !== 0 && currentPage > totalPages - remainingPages)
    );
  };

  const renderPageNavigation = () => (
    <>
      <PageNavButton
        name="previous"
        isDisabled={Number.isNaN(currentPage) || navBarDisabled}
        isZoom={isZoom}
        turnPage={turnPage(false)}
      />
      <PageSelector
        currentPage={currentPage}
        totalPages={totalPages}
        pageOffset={pageOffset}
        isZoom={isZoom}
        toggleMenu={toggleMenu}
        singlePage={singlePage}
      />
      <PageNavButton
        name="next"
        isDisabled={nextPageDisabled() || navBarDisabled}
        isZoom={isZoom}
        turnPage={turnPage(true)}
      />
    </>
  );

  const renderPageToggle = () => (
    <div className={pageToggleContainer}>
      {isZoom ? <OnePageSpread /> : <TwoPageSpread />}
      <BasicButton
        buttonStyles={[onePageToggle]}
        title={'One Page'}
        onClick={() => switchPageView(true)}
      />
      <BasicButton
        buttonStyles={[twoPageToggle]}
        title={'Two Pages'}
        onClick={() => switchPageView(false)}
      />
    </div>
  );

  const renderZoomButtons = () => (
    <div className={pageZoom}>
      <ZoomButton
        type={ZoomTypes.RESET}
        onClick={resetZoom}
        key={'resetZoom'}
        isDisabled={zoomScale === 1}
      />
      <ZoomButton
        type={ZoomTypes.OUT}
        onClick={decreaseZoom}
        key={'zoomIOut'}
        isDisabled={zoomScale <= MIN_ZOOM_SCALE}
      />
      <div className={zoomLevelText}>{Math.floor(zoomScale * 100)}%</div>
      <ZoomButton
        type={ZoomTypes.IN}
        onClick={increaseZoom}
        key={'zoomIn'}
        isDisabled={zoomScale >= MAX_ZOOM_SCALE}
      />
    </div>
  );

  const handleExitFullScreen = () => {
    setFullScreen(false);
    screen1Handle.exit();
  };

  if (isFullScreen) {
    return (
      <nav id="fullScreenNavbar" className={fullScreenNavBar}>
        {Menu}
        {renderZoomButtons()}
        <div className={verticalLine} />
        {renderPageNavigation()}
        <div className={verticalLine} />
        {renderPageToggle()}
        <div className={verticalLine} />
        <BasicButton
          buttonStyles={[stopButton]}
          title={'Exit Fullscreen'}
          onClick={handleExitFullScreen}
        >
          STOP
        </BasicButton>
      </nav>
    );
  }

  return (
    <nav className={navBar} id="bottomNavBar">
      {Menu}
      <div className={reminder}>
        <MaybeTrialBadge
          shouldRender={isTrialLicense}
          daysLeft={trialDaysLeft}
          showTrialModal={showTrialModal}
          textColor={workbook.textColor}
        />
      </div>
      <div className={mainNavBarContent}>
        {renderZoomButtons()}
        <div className={verticalLine} />
        {renderPageNavigation()}
        <div className={verticalLine} />
        {renderPageToggle()}
        <div
          className={verticalLine}
          style={{
            visibility: shouldShowSwitcher ? 'visible' : 'hidden'
          }}
        />
      </div>
      <PageToggleButton
        workbookType={workbook.type}
        handleClick={switchBooks}
        buttonClass={switcheroo}
      />
    </nav>
  );
};

const showSwitcher = (workbook: Workbook, isZoom: boolean): boolean => {
  const { pageDetails } = workbook;
  if (!pageDetails || !pageDetails.length || !isZoom) return false;

  const [pageDetail] = pageDetails;
  return pageDetail.teachersGuidePage || pageDetail.workbookPage ? true : false;
};

const useStyles = theme =>
  makeStyles(() => ({
    navBar: () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'fixed',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      backgroundColor: theme.palette.primary.main,
      zIndex: 7,
      userSelect: 'none',
      fontSize: '15px'
    }),
    mainNavBarContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: remCalc(115)
    },
    switcheroo: ({ shouldShowSwitcher }: any) => ({
      visibility: shouldShowSwitcher ? 'visible' : 'hidden'
    }),
    pageToggleContainer: () => ({
      width: remCalc(120),
      position: 'relative',
      height: remCalc(75),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }),
    onePageToggle: ({ isZoom }: any) => ({
      left: 0,
      width: remCalc(60),
      position: 'absolute',
      bottom: 0,
      height: '100%',
      cursor: isZoom ? 'default' : 'pointer'
    }),
    twoPageToggle: ({ isZoom }: any) => ({
      right: 0,
      width: remCalc(60),
      position: 'absolute',
      bottom: 0,
      height: '100%',
      cursor: isZoom ? 'pointer' : 'default'
    }),
    stopButton: {
      width: remCalc(70),
      backgroundColor: 'red',
      border: '1px black solid',
      color: 'white',
      borderRadius: remCalc(5),
      marginRight: remCalc(25),
      boxShadow: 'rgb(44,44,44) 0px 3px 7px 1px',
      '&:hover': {
        transform: 'scale(1.05, 1.05)'
      }
    },
    verticalLine: {
      height: remCalc(50),
      width: remCalc(2),
      backgroundColor: 'black',
      margin: remCalc(0, 20)
    },
    fullScreenNavBar: ({ isIdle }: any) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'fixed',
      bottom: 0,
      width: 'max-content',
      textAlign: 'center',
      backgroundColor: theme.palette.primary.main,
      zIndex: 7,
      userSelect: 'none',
      fontSize: '15px',
      transition: '0.5s',
      transform: !isIdle ? 'translateY(0)' : 'translateY(300px)'
    }),

    reminder: {
      position: 'absolute',
      left: '25px',
      fontSize: '15px'
    },
    pageZoom: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: remCalc(15)
    },
    zoomLevelText: ({ workbook }: any) => ({
      fontWeight: 400,
      color: workbook?.textColor ?? 'black',
      justifyContent: 'center',
      alignItems: 'center',
      width: '40px'
    })
  }));

PageNavBar.defaultProps = {
  singlePage: false,
  isZoom: false
};

PageNavBar.displayName = 'PageNavBar';

export default PageNavBar;
